import React, { useState } from 'react';
import Appearance from 'styles/Appearance.js';
import Utils from 'files/Utils.js';
import SectionHeader from 'views/SectionHeader.js';
import { aquaLink, checkedDate, competitors, features } from 'files/ComparisonData.js';

// Aqua Link next to popular water leak sensors (data and sources in
// files/ComparisonData.js). Desktop: the full chart in one card, Aqua Link's
// column tinted. Phones: six columns can't fit, so it compares Aqua Link with
// one competitor at a time, picked from a row of chips.
const ComparisonChart = () => {

    const mobile = Utils.isMobile();
    const [picked, setPicked] = useState(0);
    const [showSources, setShowSources] = useState(false);

    const tint = Utils.hexToRGBA(Appearance.colors.primary(), 0.05);
    const rule = `1px solid ${Appearance.colors.border()}`;
    const cellText = Appearance.textStyles.body({ fontSize: mobile ? 14 : 14.5, color: Appearance.colors.text(), lineHeight: 1.45 });
    const modelText = Appearance.textStyles.caption({ fontSize: mobile ? 12 : 12.5, color: Appearance.colors.subText(), fontWeight: 400 });
    const nameText = (color) => ({ fontFamily: Appearance.fonts.body, fontSize: mobile ? 15 : 15, fontWeight: 600, lineHeight: 1.3, color });

    // --- Desktop: the full chart ------------------------------------------
    const desktopChart = (
        <div style={{ ...Appearance.styles.card(), boxShadow: Appearance.boxShadow.card }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <colgroup>
                    <col style={{ width: '14%' }} />
                    <col />
                    {competitors.map(c => <col key={c.key} />)}
                </colgroup>
                <thead>
                    <tr>
                        <th scope={'col'} style={{ borderBottom: rule }} />
                        <th scope={'col'} style={{ textAlign: 'left', verticalAlign: 'bottom', padding: '22px 16px 16px', backgroundColor: tint, borderBottom: rule }}>
                            <div style={nameText(Appearance.colors.primary())}>{aquaLink.name}</div>
                            <div style={{ ...modelText, marginTop: 3 }}>{aquaLink.model}</div>
                        </th>
                        {competitors.map(c => (
                            <th key={c.key} scope={'col'} style={{ textAlign: 'left', verticalAlign: 'bottom', padding: '22px 16px 16px', borderBottom: rule }}>
                                <div style={nameText(Appearance.colors.text())}>{c.name}</div>
                                <div style={{ ...modelText, marginTop: 3 }}>{c.model}</div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {features.map((f, i) => {
                        const last = i === features.length - 1;
                        const border = last ? 'none' : rule;
                        return (
                            <tr key={f.key}>
                                <th scope={'row'} style={{ ...cellText, fontWeight: 600, textAlign: 'left', verticalAlign: 'top', padding: '16px 16px', borderBottom: border }}>
                                    {f.label}
                                </th>
                                <td style={{ ...cellText, fontWeight: 600, verticalAlign: 'top', padding: '16px 16px', backgroundColor: tint, borderBottom: border }}>
                                    {aquaLink.values[f.key]}
                                </td>
                                {competitors.map(c => (
                                    <td key={c.key} style={{ ...cellText, verticalAlign: 'top', padding: '16px 16px', borderBottom: border }}>
                                        {c.values[f.key]}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );

    // --- Phones: Aqua Link vs. one picked competitor ------------------------
    const competitor = competitors[picked];
    const chip = (c, index) => {
        const active = index === picked;
        return (
            <button
                key={c.key}
                type={'button'}
                aria-pressed={active}
                onClick={() => setPicked(index)}
                style={{
                    flexShrink: 0,
                    padding: '8px 13px',
                    borderRadius: 999,
                    cursor: 'pointer',
                    border: `1px solid ${active ? Appearance.colors.primary() : Appearance.colors.border()}`,
                    backgroundColor: active ? Utils.hexToRGBA(Appearance.colors.primary(), 0.08) : Appearance.colors.white,
                    color: active ? Appearance.colors.primary() : Appearance.colors.text(),
                    fontFamily: Appearance.fonts.body,
                    fontSize: 13.5,
                    fontWeight: 600,
                }}>
                {c.short}
            </button>
        );
    };

    const mobileChart = (
        <div>
            <div style={{ ...Appearance.textStyles.caption({ fontSize: 13 }), textAlign: 'center', marginBottom: 8 }}>{'Compare Aqua Link with'}</div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 7, marginBottom: 14, overflowX: 'auto', paddingBottom: 2 }}>
                {competitors.map(chip)}
            </div>

            <div style={{ ...Appearance.styles.card(), boxShadow: Appearance.boxShadow.card }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: rule }}>
                    <div style={{ padding: '16px 14px 14px', backgroundColor: tint }}>
                        <div style={nameText(Appearance.colors.primary())}>{aquaLink.name}</div>
                        <div style={{ ...modelText, marginTop: 2 }}>{aquaLink.model}</div>
                    </div>
                    <div style={{ padding: '16px 14px 14px' }}>
                        <div style={nameText(Appearance.colors.text())}>{competitor.name}</div>
                        <div style={{ ...modelText, marginTop: 2 }}>{competitor.model}</div>
                    </div>
                </div>
                {features.map((f, i) => (
                    <div key={f.key} style={{ borderBottom: i === features.length - 1 ? 'none' : rule }}>
                        {/* Label strip carries the Aqua Link tint on its left half, so the column reads unbroken */}
                        <div style={{
                            ...Appearance.textStyles.label({ fontSize: 11, color: Appearance.colors.subText() }),
                            padding: '12px 14px 0',
                            background: `linear-gradient(90deg, ${tint} 50%, transparent 50%)`,
                        }}>
                            {f.label}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                            <div style={{ ...cellText, fontWeight: 600, padding: '6px 14px 14px', backgroundColor: tint }}>
                                {aquaLink.values[f.key]}
                            </div>
                            <div style={{ ...cellText, padding: '6px 14px 14px' }}>
                                {competitor.values[f.key]}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    // --- Footnote and sources -----------------------------------------------
    const link = { color: Appearance.colors.primary(), textDecoration: 'none' };
    const sources = (
        <div style={{
            marginTop: 18,
            display: 'grid',
            gridTemplateColumns: mobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
            gap: mobile ? 16 : '18px 40px',
            textAlign: 'left',
        }}>
            {competitors.map(c => (
                <div key={c.key}>
                    <div style={{ ...Appearance.textStyles.label({ fontSize: 11, color: Appearance.colors.subText() }), marginBottom: 6 }}>{c.name}</div>
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 5 }}>
                        {c.sources.map(([label, url]) => (
                            <li key={url} style={Appearance.textStyles.caption({ fontSize: 13 })}>
                                <a href={url} target={'_blank'} rel={'noopener noreferrer'} style={link}>{label}</a>
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );

    return (
        <section id={'compare'} style={{ width: '100%', backgroundColor: Appearance.colors.offWhite }}>
            <div style={{ maxWidth: Appearance.layout.maxWidth, margin: '0 auto', padding: Appearance.layout.sectionPadding() }}>

                <SectionHeader
                    eyebrow={'Why Aqua Link'}
                    title={'How Aqua Link compares'}
                    subtitle={'Aqua Link next to popular smart water leak sensors, using each manufacturer’s published specifications.'} />

                {mobile ? mobileChart : desktopChart}

                <div style={{ textAlign: 'center', marginTop: mobile ? 18 : 22 }}>
                    <p style={{ ...Appearance.textStyles.caption({ fontSize: 12.5, color: Appearance.colors.subText() }), maxWidth: 760, margin: '0 auto' }}>
                        {`Competitor details from manufacturer product pages, spec sheets and manuals, checked ${checkedDate}. Alarm volumes are manufacturer ratings; manufacturers do not publish the distance they were measured at.`}
                    </p>
                    <button
                        type={'button'}
                        aria-expanded={showSources}
                        onClick={() => setShowSources(s => !s)}
                        style={{ ...link, background: 'none', border: 'none', cursor: 'pointer', fontFamily: Appearance.fonts.body, fontSize: 13, fontWeight: 600, marginTop: 8, padding: 4 }}>
                        {showSources ? 'Hide sources' : 'Show sources'}
                    </button>
                    {showSources && sources}
                </div>
            </div>
        </section>
    );
};

export default ComparisonChart;
